import { MAX_TRACKED_FREQUENCY, MIN_TRACKED_FREQUENCY, frequencyToMidi, midiToFrequency, type MidiNote, type MidiTrack, type PitchFrame, type TrackRole } from "./music.ts";
import { analyzeSilenceLevels, type NoiseCalibration } from "./noise.ts";
import { MicrophoneAccessError } from "./microphone.ts";
import { outputVolumeToGain } from "./output-volume.ts";
import { AUDIO_SOURCE_GAINS, playbackSourceGain } from "./audio-levels.ts";
import { METRONOME_CALLBACK_INTERVAL_MS, METRONOME_MIN_LEAD_SECONDS, METRONOME_SCHEDULE_AHEAD_SECONDS, buildCountInSchedule, planMetronomeLookahead, type CountInSchedule } from "./metronome.ts";
import { PIANO_HARMONICS, buildPreviewEnvelope, playbackRange, type PreviewInstrument } from "./preview-instrument.ts";

const ANALYSER_SIZE = 2048;
const MIN_FREQUENCY = MIN_TRACKED_FREQUENCY;
const MAX_FREQUENCY = MAX_TRACKED_FREQUENCY;
const YIN_THRESHOLD = 0.14;
const AUDIO_ATTACK_SECONDS = 0.012;
const AUDIO_RELEASE_SECONDS = 0.045;
const MAX_SUSTAINED_VOICES = 16;

type ManagedAudioVoice = {
  oscillators: OscillatorNode[];
  gains: GainNode[];
  disconnect: AudioNode[];
};

export function detectPitchYin(
  buffer: Float32Array,
  sampleRate: number,
  rmsThreshold = 0.012,
  noiseFloor = 0,
): { frequency: number | null; confidence: number; level: number } {
  const rms = Math.sqrt(buffer.reduce((sum, sample) => sum + sample * sample, 0) / buffer.length);
  const signalLevel = Math.max(0, rms - noiseFloor);
  if (signalLevel < rmsThreshold) return { frequency: null, confidence: 0, level: signalLevel };
  const minTau = Math.max(2, Math.floor(sampleRate / MAX_FREQUENCY));
  const maxTau = Math.min(Math.floor(sampleRate / MIN_FREQUENCY), Math.floor(buffer.length / 2));
  const difference = new Float32Array(maxTau + 1);
  for (let tau = minTau; tau <= maxTau; tau += 1) {
    let sum = 0;
    for (let index = 0; index < buffer.length - tau; index += 1) {
      const delta = buffer[index] - buffer[index + tau];
      sum += delta * delta;
    }
    difference[tau] = sum;
  }
  const normalized = new Float32Array(maxTau + 1);
  let running = 0;
  normalized[0] = 1;
  for (let tau = 1; tau <= maxTau; tau += 1) {
    running += difference[tau];
    normalized[tau] = running ? (difference[tau] * tau) / running : 1;
  }
  let tau = minTau;
  while (tau <= maxTau) {
    if (normalized[tau] < YIN_THRESHOLD) {
      while (tau + 1 <= maxTau && normalized[tau + 1] < normalized[tau]) tau += 1;
      const previous = normalized[tau - 1] ?? normalized[tau];
      const next = normalized[tau + 1] ?? normalized[tau];
      const denominator = 2 * (2 * normalized[tau] - next - previous);
      const adjustedTau = denominator ? tau + (next - previous) / denominator : tau;
      return { frequency: sampleRate / adjustedTau, confidence: Math.max(0, 1 - normalized[tau]), level: signalLevel };
    }
    tau += 1;
  }
  return { frequency: null, confidence: 0, level: signalLevel };
}

const waveformForRole: Record<TrackRole, OscillatorType> = {
  melody: "triangle",
  bass: "sine",
  strings: "sawtooth",
  synth: "square",
};

export type MetronomeDiagnostics = {
  callbackIntervalMs: number;
  scheduleAheadMs: number;
  maxCallbackStallMs: number;
  missedSchedulingDeadlines: number;
  pendingScheduledBeats: number;
  scheduledBeats: number;
  skippedBeats: number;
  sessionStartTime: number | null;
  lastScheduledBeatTime: number | null;
};

export type AudioDebugInfo = {
  state: AudioContextState | "unavailable";
  sampleRate: number | null;
  baseLatencyMs: number | null;
  outputLatencyMs: number | null;
  outputVolume: number;
  masterGain: number | null;
  limiterReduction: number | null;
  sourceGains: typeof AUDIO_SOURCE_GAINS;
  metronome: MetronomeDiagnostics;
  lastPlaybackError: string | null;
  contextCount: number;
  activeVoices: number;
  activeAudioNodes: number;
  scheduledNotes: number;
  playSessionId: number | null;
  warmedUp: boolean;
  microphoneState: MediaStreamTrackState | "inactive";
  inputSampleRate: number | null;
  inputChannelCount: number | null;
  recorderState: RecordingState | "unavailable";
  lastPitchProcessingMs: number | null;
  lastDetectedMidi: number | null;
};

export class AudioEngine {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private limiter: DynamicsCompressorNode | null = null;
  private stream: MediaStream | null = null;
  private analyser: AnalyserNode | null = null;
  private recorder: MediaRecorder | null = null;
  private lastPitchProcessingMs: number | null = null;
  private lastDetectedMidi: number | null = null;
  private chunks: Blob[] = [];
  private guideTimer: number | null = null;
  private guideVoices = new Set<ManagedAudioVoice>();
  private transientVoices = new Set<ManagedAudioVoice>();
  private guideScheduledBeatTimes: number[] = [];
  private metronomeDiagnostics: MetronomeDiagnostics = {
    callbackIntervalMs: METRONOME_CALLBACK_INTERVAL_MS,
    scheduleAheadMs: METRONOME_SCHEDULE_AHEAD_SECONDS * 1000,
    maxCallbackStallMs: 0,
    missedSchedulingDeadlines: 0,
    pendingScheduledBeats: 0,
    scheduledBeats: 0,
    skippedBeats: 0,
    sessionStartTime: null,
    lastScheduledBeatTime: null,
  };
  private pitchBuffer = new Float32Array(ANALYSER_SIZE);
  private sustainedVoices = new Map<number, { oscillator: OscillatorNode; gain: GainNode }>();
  private playbackVoices = new Set<ManagedAudioVoice>();
  private releasingPlaybackVoices = new Set<ManagedAudioVoice>();
  private playbackGeneration = 0;
  private nextPlaySessionId = 1;
  private activePlaySessionId: number | null = null;
  private scheduledPlaybackNotes = 0;
  private contextCount = 0;
  private nextVoiceId = 1;
  private preserveSoftTone = false;
  private lastPlaybackError: string | null = null;
  private lastDebugSignature = "";
  private outputVolume = 85;
  private warmedUp = false;
  private warmUpPromise: Promise<void> | null = null;

  private createContext(): AudioContext {
    const context = new AudioContext({ latencyHint: "interactive" });
    const masterGain = context.createGain();
    const limiter = context.createDynamicsCompressor();
    masterGain.gain.value = outputVolumeToGain(this.outputVolume);
    limiter.threshold.value = -12;
    limiter.knee.value = 18;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.18;
    masterGain.connect(limiter).connect(context.destination);
    this.context = context;
    this.masterGain = masterGain;
    this.limiter = limiter;
    this.contextCount += 1;
    return context;
  }

  private releaseVoice(voice: ManagedAudioVoice, releaseSeconds = AUDIO_RELEASE_SECONDS): void {
    const context = this.context;
    if (!context) return;
    const now = context.currentTime;
    const stopAt = now + releaseSeconds + 0.012;
    voice.gains.forEach((gain) => {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + releaseSeconds);
    });
    voice.oscillators.forEach((oscillator) => {
      try { oscillator.stop(stopAt); } catch { /* already stopped */ }
    });
  }

  private trackVoice(collection: Set<ManagedAudioVoice>, voice: ManagedAudioVoice): void {
    collection.add(voice);
    let remaining = voice.oscillators.length;
    const cleanup = () => {
      remaining -= 1;
      if (remaining > 0) return;
      collection.delete(voice);
      this.releasingPlaybackVoices.delete(voice);
      voice.disconnect.forEach((node) => {
        try { node.disconnect(); } catch { /* already disconnected */ }
      });
      if (collection === this.playbackVoices && this.playbackVoices.size === 0) {
        this.activePlaySessionId = null;
        this.scheduledPlaybackNotes = 0;
      }
    };
    voice.oscillators.forEach((oscillator) => { oscillator.onended = cleanup; });
  }

  private recordPlaybackError(caught: unknown): never {
    this.lastPlaybackError = caught instanceof Error ? caught.message : String(caught);
    console.error("[P-013 audio] playback failed", this.getDebugInfo(), caught);
    throw caught;
  }

  async ensureAudioReady(): Promise<AudioContext> {
    try {
      let context = !this.context || this.context.state === "closed" ? this.createContext() : this.context;
      if (!this.masterGain) context = this.createContext();

      const unlock = context.createBufferSource();
      unlock.buffer = context.createBuffer(1, 1, context.sampleRate);
      unlock.connect(this.masterGain as GainNode);
      unlock.onended = () => unlock.disconnect();
      unlock.start();

      if (context.state !== "running") await context.resume();
      if (context.state !== "running") throw new Error("AudioContext did not start: " + context.state);

      this.lastPlaybackError = null;
      const debug = this.getDebugInfo();
      const signature = [debug.state, debug.sampleRate, debug.outputVolume, debug.masterGain].join(":");
      if (signature !== this.lastDebugSignature) {
        console.info("[P-013 audio] ready", debug);
        this.lastDebugSignature = signature;
      }
      return context;
    } catch (caught) {
      return this.recordPlaybackError(caught);
    }
  }

  async warmUp(): Promise<void> {
    if (this.warmedUp) return;
    if (this.warmUpPromise) return this.warmUpPromise;
    this.warmUpPromise = (async () => {
      const context = await this.ensureAudioReady();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const now = context.currentTime;
      gain.gain.setValueAtTime(0.000001, now);
      oscillator.frequency.value = 440;
      oscillator.connect(gain).connect(this.output());
      oscillator.start(now);
      oscillator.stop(now + 0.025);
      await new Promise<void>((resolve) => {
        oscillator.onended = () => {
          try { oscillator.disconnect(); } catch { /* already disconnected */ }
          try { gain.disconnect(); } catch { /* already disconnected */ }
          resolve();
        };
      });
      this.warmedUp = true;
    })().finally(() => {
      this.warmUpPromise = null;
    });
    return this.warmUpPromise;
  }
  async ensureContext(): Promise<AudioContext> {
    return this.ensureAudioReady();
  }

  getContextState(): AudioContextState | "unavailable" {
    return this.context?.state ?? "unavailable";
  }

  setOutputVolume(percent: number): void {
    this.outputVolume = Math.max(0, Math.min(100, Math.round(percent)));
    if (this.masterGain && this.context) {
      const now = this.context.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setTargetAtTime(outputVolumeToGain(this.outputVolume), now, 0.025);
    }
  }

  getDebugInfo(): AudioDebugInfo {
    const context = this.context as (AudioContext & { outputLatency?: number }) | null;
    const managedVoices = [...this.playbackVoices, ...this.releasingPlaybackVoices, ...this.guideVoices, ...this.transientVoices];
    const activeAudioNodes = managedVoices.reduce((sum, voice) => sum + voice.disconnect.length, 0)
      + this.sustainedVoices.size * 2;
    const inputTrack = this.stream?.getAudioTracks()[0];
    const inputSettings = inputTrack?.getSettings?.() ?? {};
    return {
      state: this.getContextState(),
      sampleRate: context?.sampleRate ?? null,
      baseLatencyMs: context ? context.baseLatency * 1000 : null,
      outputLatencyMs: context?.outputLatency === undefined ? null : context.outputLatency * 1000,
      outputVolume: this.outputVolume,
      masterGain: this.masterGain?.gain.value ?? null,
      limiterReduction: this.limiter?.reduction ?? null,
      sourceGains: AUDIO_SOURCE_GAINS,
      metronome: { ...this.metronomeDiagnostics },
      lastPlaybackError: this.lastPlaybackError,
      contextCount: this.contextCount,
      activeVoices: this.playbackVoices.size + this.releasingPlaybackVoices.size + this.guideVoices.size + this.transientVoices.size + this.sustainedVoices.size,
      activeAudioNodes,
      scheduledNotes: this.scheduledPlaybackNotes,
      playSessionId: this.activePlaySessionId,
      warmedUp: this.warmedUp,
      microphoneState: inputTrack?.readyState ?? "inactive",
      inputSampleRate: inputSettings.sampleRate ?? null,
      inputChannelCount: inputSettings.channelCount ?? null,
      recorderState: this.recorder?.state ?? "unavailable",
      lastPitchProcessingMs: this.lastPitchProcessingMs,
      lastDetectedMidi: this.lastDetectedMidi,
    };
  }

  getCurrentTime(): number {
    return this.context?.currentTime ?? 0;
  }

  async waitUntilAudioTime(targetTime: number, options: { signal?: AbortSignal; stallTimeoutMs?: number } = {}): Promise<void> {
    const context = await this.ensureContext();
    let lastAudioTime = context.currentTime;
    let lastProgressAt = performance.now();
    let recoveryAttempts = 0;
    while (context.currentTime < targetTime) {
      if (options.signal?.aborted) {
        const cancelled = new Error("Audio wait cancelled");
        cancelled.name = "AbortError";
        throw cancelled;
      }
      if (context.state !== "running") await context.resume();
      const currentAudioTime = context.currentTime;
      if (currentAudioTime > lastAudioTime + 0.0001) {
        lastAudioTime = currentAudioTime;
        lastProgressAt = performance.now();
        recoveryAttempts = 0;
      } else if (performance.now() - lastProgressAt >= (options.stallTimeoutMs ?? 900)) {
        // iOS may suspend/interrupt AudioContext when MediaStream is reacquired.
        // Resume during the wait instead of leaving count-in frozen at 4 forever.
        await context.resume();
        recoveryAttempts += 1;
        lastProgressAt = performance.now();
        if (context.state !== "running" || recoveryAttempts >= 3) {
          throw new Error("AudioContext clock stopped during count-in");
        }
      }
      await new Promise((resolve) => window.setTimeout(resolve, Math.min(25, Math.max(4, (targetTime - context.currentTime) * 500))));
    }
  }

  private output(): GainNode {
    if (!this.masterGain) throw new Error("Audio output graph is not ready");
    return this.masterGain;
  }

  async prepareMicrophone(preserveSoftTone = false): Promise<void> {
    this.releaseMicrophone();
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new MicrophoneAccessError("unsupported", "navigator.mediaDevices.getUserMedia is unavailable");
    }
    let context: AudioContext;
    try {
      context = await this.ensureContext();
    } catch (caught) {
      const detail = caught instanceof Error ? caught.message : String(caught);
      throw new MicrophoneAccessError("audio-context-failed", detail);
    }
    this.preserveSoftTone = preserveSoftTone;
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: !preserveSoftTone, autoGainControl: preserveSoftTone },
    });
    if (!this.stream.getAudioTracks().length) {
      this.releaseMicrophone();
      throw new MicrophoneAccessError("not-found", "getUserMedia returned no audio track");
    }
    const source = context.createMediaStreamSource(this.stream);
    this.analyser = context.createAnalyser();
    this.analyser.fftSize = ANALYSER_SIZE;
    this.analyser.smoothingTimeConstant = 0;
    source.connect(this.analyser);
    if (navigator.mediaDevices.enumerateDevices) {
      void navigator.mediaDevices.enumerateDevices()
        .then((devices) => console.info("[P-013 microphone] audio inputs", devices.filter((device) => device.kind === "audioinput").length))
        .catch((caught) => console.warn("[P-013 microphone] enumerateDevices failed", caught));
    }
  }

  releaseMicrophone(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.analyser = null;
  }

  getInputInfo(): { deviceLabel: string; sampleRate: number; channelCount: number; preserveSoftTone: boolean } {
    const track = this.stream?.getAudioTracks()[0];
    const settings = track?.getSettings?.() ?? {};
    return {
      deviceLabel: track?.label || "既定のマイク",
      sampleRate: settings.sampleRate ?? this.context?.sampleRate ?? 44100,
      channelCount: settings.channelCount ?? 1,
      preserveSoftTone: this.preserveSoftTone,
    };
  }

  private readInputLevel(): number {
    if (!this.analyser) return 0;
    this.analyser.getFloatTimeDomainData(this.pitchBuffer);
    return Math.sqrt(this.pitchBuffer.reduce((sum, sample) => sum + sample * sample, 0) / this.pitchBuffer.length);
  }

  async calibrateSilence(durationMs = 3000): Promise<NoiseCalibration> {
    if (!this.stream || !this.analyser || !this.context) throw new Error("Microphone is not ready");
    const levels: number[] = [];
    const sampleCount = Math.max(20, Math.round(durationMs / 50));
    for (let index = 0; index < sampleCount; index += 1) {
      levels.push(this.readInputLevel());
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    const track = this.stream.getAudioTracks()[0];
    const settings = track?.getSettings?.() ?? {};
    return {
      ...analyzeSilenceLevels(levels),
      deviceLabel: track?.label || "既定のマイク",
      sampleRate: settings.sampleRate ?? this.context.sampleRate,
      channelCount: settings.channelCount ?? 1,
      preserveSoftTone: this.preserveSoftTone,
    };
  }

  startCapture(): void {
    if (!this.stream) throw new Error("Microphone is not ready");
    if (typeof MediaRecorder === "undefined") {
      throw new MicrophoneAccessError("recorder-failed", "MediaRecorder is unavailable");
    }
    this.chunks = [];
    // Android WebView can advertise MP4 recording while its HTMLMediaElement cannot decode
    // the resulting fragment. Prefer WebM/Opus because the same WebView stack can replay it.
    const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((type) => MediaRecorder.isTypeSupported(type));
    try {
      this.recorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    } catch (caught) {
      const detail = caught instanceof Error ? caught.message : String(caught);
      throw new MicrophoneAccessError("recorder-failed", detail);
    }
    this.recorder.ondataavailable = (event) => {
      if (event.data.size) this.chunks.push(event.data);
    };
    this.recorder.start(250);
  }

  samplePitch(time: number, rmsThreshold = 0.012, noiseFloor = 0): PitchFrame {
    const processingStartedAt = performance.now();
    if (!this.analyser || !this.context) return { time, frequency: null, midiFloat: null, confidence: 0, level: 0 };
    this.analyser.getFloatTimeDomainData(this.pitchBuffer);
    const result = detectPitchYin(this.pitchBuffer, this.context.sampleRate, rmsThreshold, noiseFloor);
    const frame = {
      time,
      frequency: result.frequency,
      midiFloat: result.frequency ? frequencyToMidi(result.frequency) : null,
      confidence: result.confidence,
      level: result.level,
    };
    this.lastPitchProcessingMs = performance.now() - processingStartedAt;
    this.lastDetectedMidi = frame.midiFloat === null ? null : Math.round(frame.midiFloat);
    return frame;
  }

  async stopCapture(): Promise<Blob | null> {
    this.stopAllAudio();
    const recorder = this.recorder;
    const blob = await new Promise<Blob | null>((resolve) => {
      if (!recorder || recorder.state === "inactive") return resolve(null);
      recorder.addEventListener("stop", () => resolve(new Blob(this.chunks, { type: recorder.mimeType || "audio/webm" })), { once: true });
      recorder.stop();
    });
    this.releaseMicrophone();
    this.recorder = null;
    if (!blob || blob.size === 0) {
      console.error("[P-013 audio] MediaRecorder returned an empty source", { mimeType: recorder?.mimeType ?? null, chunkCount: this.chunks.length });
      return null;
    }
    console.info("[P-013 audio] captured replayable source", { mimeType: blob.type, size: blob.size, chunkCount: this.chunks.length });
    return blob;
  }

  async playTone(midi: number, duration = 0.35, volume: number = AUDIO_SOURCE_GAINS.piano, startDelay = 0): Promise<void> {
    const context = await this.ensureContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const start = context.currentTime + startDelay;
    oscillator.type = "triangle";
    oscillator.frequency.value = midiToFrequency(midi);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(this.output());
    this.trackVoice(this.transientVoices, { oscillators: [oscillator], gains: [gain], disconnect: [oscillator, gain] });
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  async startSustainedTone(midi: number, volume: number = AUDIO_SOURCE_GAINS.sustainedPiano): Promise<number> {
    const context = await this.ensureContext();
    while (this.sustainedVoices.size >= MAX_SUSTAINED_VOICES) {
      const oldest = this.sustainedVoices.keys().next().value;
      if (oldest === undefined) break;
      this.stopSustainedTone(oldest);
    }
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    const voiceId = this.nextVoiceId++;
    oscillator.type = "triangle";
    oscillator.frequency.value = midiToFrequency(midi);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.018);
    oscillator.connect(gain).connect(this.output());
    oscillator.onended = () => {
      this.sustainedVoices.delete(voiceId);
      try { oscillator.disconnect(); } catch { /* already disconnected */ }
      try { gain.disconnect(); } catch { /* already disconnected */ }
    };
    oscillator.start(now);
    this.sustainedVoices.set(voiceId, { oscillator, gain });
    return voiceId;
  }

  stopSustainedTone(voiceId: number): void {
    const voice = this.sustainedVoices.get(voiceId);
    if (!voice || !this.context) return;
    const now = this.context.currentTime;
    voice.gain.gain.cancelScheduledValues(now);
    voice.gain.gain.setValueAtTime(Math.max(0.0001, voice.gain.gain.value), now);
    voice.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
    try { voice.oscillator.stop(now + 0.1); } catch { /* already stopped */ }
    this.sustainedVoices.delete(voiceId);
  }

  private scheduleClickAt(context: AudioContext, startTime: number, accent: boolean, tonicIndex: number | null, guide = false): void {
    const start = Math.max(startTime, context.currentTime + 0.004);
    const rootMidi = 72 + (tonicIndex ?? 0);
    const frequencies = tonicIndex === null
      ? (accent ? [1760, 1320] : [1280, 960])
      : (accent ? [midiToFrequency(rootMidi), midiToFrequency(rootMidi + 7)] : [midiToFrequency(rootMidi + 7), midiToFrequency(rootMidi + 12)]);
    frequencies.forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "square";
      oscillator.frequency.value = frequency;
      const sourceGain = accent ? AUDIO_SOURCE_GAINS.clickAccent : AUDIO_SOURCE_GAINS.clickRegular;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(sourceGain * (index === 0 ? 1 : 0.52), start + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + (accent ? 0.095 : 0.072));
      oscillator.connect(gain).connect(this.output());
      this.trackVoice(guide ? this.guideVoices : this.transientVoices, {
        oscillators: [oscillator], gains: [gain], disconnect: [oscillator, gain],
      });
      oscillator.start(start);
      oscillator.stop(start + (accent ? 0.105 : 0.082));
    });
  }

  private scheduleGuideToneAt(context: AudioContext, midi: number, startTime: number): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.value = midiToFrequency(midi);
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(AUDIO_SOURCE_GAINS.reference, startTime + AUDIO_ATTACK_SECONDS);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.42);
    oscillator.connect(gain).connect(this.output());
    this.trackVoice(this.guideVoices, { oscillators: [oscillator], gains: [gain], disconnect: [oscillator, gain] });
    oscillator.start(startTime);
    oscillator.stop(startTime + 0.44);
  }

  async playClick(accent: boolean, tonicIndex: number | null = null): Promise<void> {
    const context = await this.ensureContext();
    this.scheduleClickAt(context, context.currentTime + 0.012, accent, tonicIndex);
  }

  async scheduleCountIn(bpm: number, tonicIndex: number | null = null, beats = 4, emitClicks = true): Promise<CountInSchedule> {
    const context = await this.ensureContext();
    const outputLatency = (context as AudioContext & { outputLatency?: number }).outputLatency ?? 0;
    const schedule = buildCountInSchedule({
      now: context.currentTime,
      bpm,
      beats,
      audioState: context.state,
      baseLatency: context.baseLatency,
      outputLatency,
    });
    if (emitClicks) schedule.beatTimes.forEach((time, index) => this.scheduleClickAt(context, time, index === 0, tonicIndex));
    return schedule;
  }

  startGuide(bpm: number, referenceMidi: number, referenceBars: number, tonicIndex: number | null = null, sessionStartTime?: number): void {
    this.stopGuide();
    const context = this.context;
    if (!context || context.state !== "running") throw new Error("Audio output is not ready for metronome");
    const outputLatency = (context as AudioContext & { outputLatency?: number }).outputLatency ?? 0;
    const secondsPerBeat = 60 / bpm;
    const startTime = sessionStartTime ?? context.currentTime + Math.max(0.06, ((context.baseLatency ?? 0) + outputLatency) * 2);
    let nextBeatIndex = 0;
    let lastCallbackAt = performance.now();
    this.metronomeDiagnostics = {
      callbackIntervalMs: METRONOME_CALLBACK_INTERVAL_MS,
      scheduleAheadMs: METRONOME_SCHEDULE_AHEAD_SECONDS * 1000,
      maxCallbackStallMs: 0,
      missedSchedulingDeadlines: 0,
      pendingScheduledBeats: 0,
      scheduledBeats: 0,
      skippedBeats: 0,
      sessionStartTime: startTime,
      lastScheduledBeatTime: null,
    };

    if (startTime >= context.currentTime + METRONOME_MIN_LEAD_SECONDS) {
      this.scheduleClickAt(context, startTime, true, tonicIndex, true);
      this.guideScheduledBeatTimes.push(startTime);
      this.metronomeDiagnostics.scheduledBeats = 1;
      this.metronomeDiagnostics.pendingScheduledBeats = 1;
      this.metronomeDiagnostics.lastScheduledBeatTime = startTime;
      nextBeatIndex = 1;
    }

    const tick = () => {
      const callbackAt = performance.now();
      const callbackStall = Math.max(0, callbackAt - lastCallbackAt - METRONOME_CALLBACK_INTERVAL_MS);
      lastCallbackAt = callbackAt;
      this.metronomeDiagnostics.maxCallbackStallMs = Math.max(this.metronomeDiagnostics.maxCallbackStallMs, callbackStall);
      const plan = planMetronomeLookahead({
        now: context.currentTime,
        sessionStartTime: startTime,
        secondsPerBeat,
        nextBeatIndex,
      });
      nextBeatIndex = plan.nextBeatIndex;
      if (plan.skippedBeatCount) {
        this.metronomeDiagnostics.skippedBeats += plan.skippedBeatCount;
        this.metronomeDiagnostics.missedSchedulingDeadlines += 1;
      }
      plan.scheduled.forEach(({ beatIndex, time }) => {
        this.scheduleClickAt(context, time, beatIndex % 4 === 0, tonicIndex, true);
        if (referenceBars > 0 && beatIndex > 0 && beatIndex % (referenceBars * 4) === 0) this.scheduleGuideToneAt(context, referenceMidi, time);
        this.guideScheduledBeatTimes.push(time);
        this.metronomeDiagnostics.scheduledBeats += 1;
        this.metronomeDiagnostics.lastScheduledBeatTime = time;
      });
      this.guideScheduledBeatTimes = this.guideScheduledBeatTimes.filter((time) => time > context.currentTime);
      this.metronomeDiagnostics.pendingScheduledBeats = this.guideScheduledBeatTimes.length;
      this.guideTimer = window.setTimeout(tick, METRONOME_CALLBACK_INTERVAL_MS);
    };
    tick();
  }

  stopGuide(): void {
    if (this.guideTimer !== null) window.clearTimeout(this.guideTimer);
    this.guideTimer = null;
    const voices = [...this.guideVoices];
    this.guideVoices.clear();
    voices.forEach((voice) => this.releaseVoice(voice));
    this.guideScheduledBeatTimes = [];
    this.metronomeDiagnostics.pendingScheduledBeats = 0;
  }

  private schedulePlaybackTone(note: MidiNote, role: TrackRole, secondsPerBeat: number, context: AudioContext, originTime: number): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const start = originTime + note.startBeat * secondsPerBeat;
    const duration = Math.max(0.05, note.durationBeats * secondsPerBeat * 0.94);
    const attack = role === "strings" ? 0.045 : 0.012;
    oscillator.type = waveformForRole[role];
    oscillator.frequency.value = midiToFrequency(note.midi);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(playbackSourceGain(role, note.velocity), start + Math.min(attack, duration / 2));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(this.output());
    this.trackVoice(this.playbackVoices, { oscillators: [oscillator], gains: [gain], disconnect: [oscillator, gain] });
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  private schedulePreviewTone(note: MidiNote, instrument: PreviewInstrument, secondsPerBeat: number, context: AudioContext, originTime = context.currentTime): void {
    const start = originTime + note.startBeat * secondsPerBeat;
    const duration = Math.max(0.05, note.durationBeats * secondsPerBeat);
    const baseGain = playbackSourceGain("melody", note.velocity);
    const envelope = buildPreviewEnvelope(instrument, duration);
    const gain = context.createGain();
    const filter = context.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = instrument === "piano" ? Math.min(6200, 2100 + note.midi * 38) : 3900;
    filter.Q.value = instrument === "piano" ? 0.72 : 0.35;
    envelope.forEach((point, index) => {
      const value = Math.max(0.000001, baseGain * point.gainRatio);
      if (index === 0) gain.gain.setValueAtTime(value, start + point.atSeconds);
      else gain.gain.exponentialRampToValueAtTime(value, start + point.atSeconds);
    });
    gain.connect(this.output());
    filter.connect(gain);

    const harmonics = instrument === "piano"
      ? PIANO_HARMONICS
      : [{ ratio: 1, level: 1, detune: 0 }] as const;
    const oscillators: OscillatorNode[] = [];
    const harmonicGains: GainNode[] = [];
    harmonics.forEach((harmonic) => {
      const oscillator = context.createOscillator();
      const harmonicGain = context.createGain();
      oscillator.type = instrument === "piano" ? "sine" : "triangle";
      oscillator.frequency.value = Math.min(context.sampleRate * 0.45, midiToFrequency(note.midi) * harmonic.ratio);
      oscillator.detune.value = harmonic.detune;
      harmonicGain.gain.value = harmonic.level;
      oscillator.connect(harmonicGain).connect(filter);
      oscillators.push(oscillator);
      harmonicGains.push(harmonicGain);
      oscillator.start(start);
      oscillator.stop(start + duration + 0.02);
    });
    this.trackVoice(this.playbackVoices, {
      oscillators,
      gains: [gain],
      disconnect: [...oscillators, ...harmonicGains, filter, gain],
    });
  }

  async playPreviewMidi(notes: MidiNote[], bpm: number, startBeat: number, instrument: PreviewInstrument): Promise<number> {
    this.stopPlayback();
    const generation = this.playbackGeneration;
    const earliestBeat = notes.length ? Math.min(0, ...notes.map((note) => note.startBeat)) : 0;
    const safeStartBeat = Math.max(earliestBeat, startBeat);
    const range = playbackRange(notes, safeStartBeat);
    if (!range.audible.length || range.remainingBeats <= 0) return 0;
    const context = await this.ensureContext();
    if (generation !== this.playbackGeneration) return 0;
    const secondsPerBeat = 60 / bpm;
    const originTime = context.currentTime + (this.releasingPlaybackVoices.size ? AUDIO_RELEASE_SECONDS + 0.02 : 0.012);
    const playSessionId = this.nextPlaySessionId++;
    this.activePlaySessionId = playSessionId;
    this.scheduledPlaybackNotes = range.audible.length;
    range.audible.forEach((note) => {
      const audibleStart = Math.max(safeStartBeat, note.startBeat);
      this.schedulePreviewTone({
        ...note,
        startBeat: audibleStart - safeStartBeat,
        durationBeats: note.startBeat + note.durationBeats - audibleStart,
      }, instrument, secondsPerBeat, context, originTime);
    });
    if (process.env.NODE_ENV !== "production") console.debug("[P-013 playback] start", this.getDebugInfo());
    return range.remainingBeats * secondsPerBeat * 1000;
  }

  async schedulePreviewRangeAt(notes: MidiNote[], bpm: number, startBeat: number, endBeat: number, startAudioTime: number, instrument: PreviewInstrument = "piano"): Promise<number> {
    const context = await this.ensureContext();
    const secondsPerBeat = 60 / bpm;
    const audible = notes.filter((note) => note.startBeat < endBeat && note.startBeat + note.durationBeats > startBeat);
    audible.forEach((note) => {
      const audibleStart = Math.max(startBeat, note.startBeat);
      this.schedulePreviewTone({
        ...note,
        startBeat: audibleStart - startBeat,
        durationBeats: Math.min(endBeat, note.startBeat + note.durationBeats) - audibleStart,
      }, instrument, secondsPerBeat, context, startAudioTime);
    });
    return Math.max(0, endBeat - startBeat) * secondsPerBeat * 1000;
  }

  async playTracks(tracks: MidiTrack[], bpm: number): Promise<number> {
    this.stopPlayback();
    const generation = this.playbackGeneration;
    const playable = tracks.filter((track) => track.notes.length);
    if (!playable.length) return 0;
    const context = await this.ensureContext();
    if (generation !== this.playbackGeneration) return 0;
    const secondsPerBeat = 60 / bpm;
    const earliestBeat = Math.min(0, ...playable.flatMap((track) => track.notes.map((note) => note.startBeat)));
    const originTime = context.currentTime + (this.releasingPlaybackVoices.size ? AUDIO_RELEASE_SECONDS + 0.02 : 0.012);
    this.activePlaySessionId = this.nextPlaySessionId++;
    this.scheduledPlaybackNotes = playable.reduce((sum, track) => sum + track.notes.length, 0);
    playable.forEach((track) => track.notes.forEach((note) => this.schedulePlaybackTone({ ...note, startBeat: note.startBeat - earliestBeat }, track.role, secondsPerBeat, context, originTime)));
    const endBeat = Math.max(...playable.flatMap((track) => track.notes.map((note) => note.startBeat + note.durationBeats)));
    if (process.env.NODE_ENV !== "production") console.debug("[P-013 playback] start", this.getDebugInfo());
    return (endBeat - earliestBeat) * secondsPerBeat * 1000;
  }

  async playMidi(notes: MidiNote[], bpm: number, role: TrackRole = "melody"): Promise<number> {
    return this.playTracks([{ name: "Preview", role, notes }], bpm);
  }

  stopPlayback(): void {
    this.playbackGeneration += 1;
    const voices = [...this.playbackVoices];
    this.playbackVoices.clear();
    this.activePlaySessionId = null;
    this.scheduledPlaybackNotes = 0;
    voices.forEach((voice) => {
      this.releasingPlaybackVoices.add(voice);
      this.releaseVoice(voice);
    });
    if (process.env.NODE_ENV !== "production" && voices.length) console.debug("[P-013 playback] stop", this.getDebugInfo());
  }

  stopTransientAudio(): void {
    const voices = [...this.transientVoices];
    this.transientVoices.clear();
    voices.forEach((voice) => this.releaseVoice(voice));
  }

  stopAllAudio(): void {
    this.stopGuide();
    this.stopPlayback();
    this.stopTransientAudio();
    [...this.sustainedVoices.keys()].forEach((voiceId) => this.stopSustainedTone(voiceId));
  }

  async dispose(): Promise<void> {
    this.stopAllAudio();
    this.releaseMicrophone();
    if (this.context && this.context.state !== "closed") await this.context.close();
    this.context = null;
    this.masterGain = null;
    this.limiter = null;
    this.warmedUp = false;
    this.warmUpPromise = null;
  }
}
